/**
 * Per-line discount in integer cents — must match the SQL triggers exactly
 * (tr_booking_service_to_invoice_line / tr_booking_addon_to_invoice_line) so the
 * displayed line nets reconcile with the invoice header to the cent.
 *
 *   gross = round(price * 100)
 *   fixed:      round(value * 100)
 *   percentage: round(gross * value / 100)
 *   (legacy services with only a resolved discount_amount fall back to it)
 * capped to [0, gross].
 */
export function lineDiscountCents(
  priceDollars: number | null | undefined,
  discountType?: string | null,
  discountValue?: number | null,
  fallbackAmountDollars?: number | null,
): number {
  const gross = Math.round((Number(priceDollars) || 0) * 100);
  let d: number;
  if (discountType === 'percentage') {
    d = Math.round((gross * (Number(discountValue) || 0)) / 100);
  } else if (discountType === 'fixed') {
    d = Math.round((Number(discountValue) || 0) * 100);
  } else {
    d = Math.round((Number(fallbackAmountDollars) || 0) * 100);
  }
  return Math.min(Math.max(d, 0), gross);
}

export interface LineNet {
  grossDollars: number;
  netDollars: number;
  discounted: boolean;
}

export function serviceLineNet(service: any): LineNet {
  const gross = Math.round((Number(service?.price) || 0) * 100);
  const d = lineDiscountCents(service?.price, service?.discount_type, service?.discount_value, service?.discount_amount);
  return { grossDollars: gross / 100, netDollars: (gross - d) / 100, discounted: d > 0 };
}

export function addonLineNet(addon: any): LineNet {
  const gross = Math.round((Number(addon?.price) || 0) * 100);
  const d = lineDiscountCents(addon?.price, addon?.discount_type, addon?.discount_value);
  return { grossDollars: gross / 100, netDollars: (gross - d) / 100, discounted: d > 0 };
}

export interface CompareAtDisplay {
  /** Bold price: the real amount after any actual discount (never affected by compare-at). */
  net: number;
  /** Strikethrough "was" value, or null when nothing should be struck through. */
  struck: number | null;
}

/**
 * The one display rule for compare-at, used by every surface.
 *
 *   net    = max(price - discountAmount, 0)               // always the bold price
 *   struck = compareAt (when compareAt > price)           // compare-at wins the slot
 *            else price (when a real discount exists)     // existing discount behaviour
 *            else null
 *
 * Compare-at is display-only — it must never feed into totals/invoices/payments.
 */
export function compareAtDisplay(
  priceDollars: number | null | undefined,
  discountAmountDollars: number | null | undefined,
  compareAt?: number | null,
): CompareAtDisplay {
  const price = Number(priceDollars) || 0;
  const disc = Math.max(Number(discountAmountDollars) || 0, 0);
  const net = Math.max(price - disc, 0);
  const hasCompare = compareAt != null && Number(compareAt) > price;
  const struck = hasCompare ? Number(compareAt) : (disc > 0 ? price : null);
  return { net, struck };
}

/** Compare-at display for a booking/proposal service object (cents-accurate discount). */
export function serviceCompareAtDisplay(service: any): CompareAtDisplay {
  const n = serviceLineNet(service);
  const discountDollars = n.grossDollars - n.netDollars;
  return compareAtDisplay(n.grossDollars, discountDollars, service?.compare_at_price);
}

/** Σ of all service + add-on line discounts (cents) for a booking's line list. */
export function totalLineDiscountCents(lineItems: any[]): number {
  return (lineItems || []).reduce((sum: number, s: any) => {
    let t = lineDiscountCents(s?.price, s?.discount_type, s?.discount_value, s?.discount_amount);
    for (const a of (s?.add_ons || s?.addons || [])) {
      t += lineDiscountCents(a?.price, a?.discount_type, a?.discount_value);
    }
    return sum + t;
  }, 0);
}
